import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner';
import LoadingOverlay from '../../../components/LoadingOverlay';
import EmptyState from '../../../components/EmptyState';
import { categoryService } from '../services/categoryService';

interface Group { _id: string; name: string; showInEvolution: boolean; showInBudget: boolean; showInPayment: boolean; showInProvisioning: boolean; showInBudgetTotal: boolean; }
interface Classifier { _id: string; groupId: string; name: string; description: string; showInEvolution: boolean; showInBudget: boolean; showInPayment: boolean; showInProvisioning: boolean; showInBudgetTotal: boolean; }
interface DescRow { description: string; groupId: string; classifierId: string; groupName: string; classifierName: string; count: number; }

type EditField = 'name' | 'description' | 'groupId';
const distinct = (values: string[]) => Array.from(new Set(values)).filter(v => v).sort((a, b) => a.localeCompare(b));

function getRange(start: number, end: number): Set<number> {
  const s = new Set<number>();
  for (let i = Math.min(start, end); i <= Math.max(start, end); i++) s.add(i);
  return s;
}

const CategoriesPage: React.FC = () => {
  const [tab, setTab] = useState<'classifications' | 'descriptions'>('classifications');
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<Group[]>([]);
  const [classifiers, setClassifiers] = useState<Classifier[]>([]);
  const [descData, setDescData] = useState<DescRow[]>([]);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    const [groupsRes, classifiersRes, descRes] = await Promise.all([
      categoryService.listGroups(),
      categoryService.listClassifiers(),
      categoryService.getDescriptionCategories(),
    ]);
    setGroups(groupsRes.data.data);
    setClassifiers(classifiersRes.data.data);
    setDescData(descRes.data.data);
  };

  useEffect(() => { loadAll().finally(() => setLoading(false)); }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader title="Categorias" />
      <div className="px-4 pt-2">
        <div className="flex gap-1 border-b mb-4">
          <button onClick={() => setTab('classifications')} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'classifications' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            Classificações
          </button>
          <button onClick={() => setTab('descriptions')} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'descriptions' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            Descritivos
          </button>
        </div>
      </div>

      {tab === 'classifications' && (
        <ClassificationsTab groups={groups} classifiers={classifiers} saving={saving} setSaving={setSaving} onReload={loadAll} />
      )}

      {tab === 'descriptions' && (
        <DescriptionsTab data={descData} saving={saving} setSaving={setSaving} onReload={loadAll} groups={groups} classifiers={classifiers} />
      )}

      <LoadingOverlay isVisible={saving} message="Processando..." />
    </>
  );
};

// === Classifications Tab ===

const ClassificationsTab: React.FC<{
  groups: Group[];
  classifiers: Classifier[];
  saving: boolean;
  setSaving: (v: boolean) => void;
  onReload: () => Promise<void>;
}> = ({ groups, classifiers, saving, setSaving, onReload }) => {
  const [newGroupName, setNewGroupName] = useState('');
  const [newClassifierName, setNewClassifierName] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [showGroupList, setShowGroupList] = useState(false);
  const [editing, setEditing] = useState<{ id: string; field: EditField } | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleGroupVisibility = async (id: string, field: string, value: boolean) => {
    setSaving(true);
    await categoryService.updateGroupVisibility(id, field, value);
    await onReload();
    setSaving(false);
  };

  const handleClassifierVisibility = async (id: string, field: string, value: boolean) => {
    setSaving(true);
    await categoryService.updateClassifierVisibility(id, field, value);
    await onReload();
    setSaving(false);
  };

  const handleDeleteGroup = async (id: string, name: string) => {
    if (!window.confirm(`Apagar agrupador "${name}" e todas suas classificações?`)) return;
    setSaving(true);
    await categoryService.deleteGroup(id);
    await onReload();
    setSaving(false);
  };

  const handleDeleteClassifier = async (id: string, name: string) => {
    if (!window.confirm(`Apagar "${name}"?`)) return;
    setSaving(true);
    await categoryService.deleteClassifier(id);
    await onReload();
    setSaving(false);
  };

  const grouped = useMemo(() => {
    const withGroup = groups.map(grp => ({
      ...grp,
      items: classifiers.filter(c => c.groupId === grp._id).sort((a, b) => a.name.localeCompare(b.name)),
    }));
    const ungrouped = classifiers.filter(c => !c.groupId || !groups.find(g => g._id === c.groupId)).sort((a, b) => a.name.localeCompare(b.name));
    return { withGroup, ungrouped };
  }, [groups, classifiers]);

  const handleAdd = async () => {
    if (!newClassifierName.trim() && !newGroupName.trim()) return;
    setSaving(true);
    if (newClassifierName.trim()) {
      if (selectedGroupId) {
        await categoryService.createClassifier(selectedGroupId, newClassifierName.trim());
      } else if (newGroupName.trim()) {
        const res = await categoryService.createGroup(newGroupName.trim());
        await categoryService.createClassifier(res.data.data._id, newClassifierName.trim());
      } else {
        await categoryService.createClassifier('', newClassifierName.trim());
      }
    } else {
      await categoryService.createGroup(newGroupName.trim());
    }
    await onReload();
    setNewClassifierName('');
    setNewGroupName('');
    setSelectedGroupId('');
    setSaving(false);
  };

  const selectGroup = (grp: Group) => {
    setSelectedGroupId(grp._id);
    setNewGroupName(grp.name);
    setShowGroupList(false);
  };

  const startEdit = (id: string, field: EditField, currentValue: string) => {
    setEditing({ id, field });
    setEditValue(currentValue);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.field === 'name' && editValue.trim()) {
        await categoryService.updateClassifier(editing.id, { name: editValue.trim() });
      } else if (editing.field === 'description') {
        await categoryService.updateClassifier(editing.id, { description: editValue.trim() });
      } else if (editing.field === 'groupId') {
        await categoryService.updateClassifier(editing.id, { groupId: editValue });
      }
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Erro ao salvar');
    }
    setEditing(null);
    await onReload();
    setSaving(false);
  };

  return (
    <div className="px-4 pb-4">
      {/* Add form */}
      <div className="flex flex-col gap-2 mb-4 p-3 bg-gray-50 rounded-lg border">
        <div className="relative">
          <div className="flex">
            <input value={newGroupName} onChange={e => { setNewGroupName(e.target.value); setSelectedGroupId(''); setShowGroupList(false); }}
              placeholder="Agrupador" className="w-full border border-gray-300 rounded-l-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            <button type="button" onClick={() => setShowGroupList(!showGroupList)}
              className="px-2 border border-l-0 border-gray-300 rounded-r-lg bg-gray-50 hover:bg-gray-100 text-gray-500 text-xs">▼</button>
          </div>
          {showGroupList && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {groups.filter(a => !newGroupName || a.name.toLowerCase().includes(newGroupName.toLowerCase())).map(opt => (
                <button key={opt._id} type="button" onClick={() => selectGroup(opt)}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 hover:text-blue-700">{opt.name}</button>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <input value={newClassifierName} onChange={e => setNewClassifierName(e.target.value)}
            placeholder="Classificação" className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }} />
          <button onClick={handleAdd} disabled={!newClassifierName.trim() && !newGroupName.trim()}
            className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 shrink-0">
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Adicionar</span>
          </button>
        </div>
      </div>

      {/* Grouped list */}
      {grouped.withGroup.length === 0 && grouped.ungrouped.length === 0 ? (
        <EmptyState title="Nenhuma classificação cadastrada" description="Crie a primeira classificação" />
      ) : (
        <div className="space-y-3">
          {grouped.withGroup.map(grp => (
            <div key={grp._id} className="border rounded-lg">
              {/* Group header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 py-2 bg-blue-50 border-b gap-1">
                <span className="font-semibold text-blue-800 text-sm">{grp.name}</span>
                <div className="flex items-center gap-2 flex-wrap">
                  <VisCheckbox label="Evo" checked={grp.showInEvolution !== false} onChange={v => handleGroupVisibility(grp._id, 'showInEvolution', v)} className="text-blue-700" />
                  <VisCheckbox label="Orç" checked={grp.showInBudget !== false} onChange={v => handleGroupVisibility(grp._id, 'showInBudget', v)} className="text-blue-700" />
                  <VisCheckbox label="Total" checked={grp.showInBudgetTotal !== false} onChange={v => handleGroupVisibility(grp._id, 'showInBudgetTotal', v)} className="text-blue-700" />
                  <VisCheckbox label="Pag" checked={grp.showInPayment !== false} onChange={v => handleGroupVisibility(grp._id, 'showInPayment', v)} className="text-blue-700" />
                  <VisCheckbox label="Prov" checked={grp.showInProvisioning !== false} onChange={v => handleGroupVisibility(grp._id, 'showInProvisioning', v)} className="text-blue-700" />
                  <button onClick={() => handleDeleteGroup(grp._id, grp.name)}
                    className="text-red-400 hover:text-red-600 p-1 ml-auto sm:ml-0" title="Apagar agrupador">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {/* Classifier items */}
              <div className="divide-y">
                {grp.items.map(cls => (
                  <ClassifierRow key={cls._id} cls={cls} groups={groups} editing={editing} editValue={editValue} setEditValue={setEditValue} saveEdit={saveEdit} setEditing={setEditing} startEdit={startEdit} onVisChange={handleClassifierVisibility} onDelete={handleDeleteClassifier} />
                ))}
              </div>
            </div>
          ))}
          {grouped.ungrouped.length > 0 && (
            <div className="border rounded-lg">
              <div className="px-3 py-2 bg-gray-100 border-b">
                <span className="font-semibold text-gray-600 text-sm">Sem agrupador</span>
              </div>
              <div className="divide-y">
                {grouped.ungrouped.map(cls => (
                  <ClassifierRow key={cls._id} cls={cls} groups={groups} editing={editing} editValue={editValue} setEditValue={setEditValue} saveEdit={saveEdit} setEditing={setEditing} startEdit={startEdit} onVisChange={handleClassifierVisibility} onDelete={handleDeleteClassifier} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// === ClassifierRow ===
const ClassifierRow: React.FC<{
  cls: Classifier;
  groups: Group[];
  editing: { id: string; field: EditField } | null;
  editValue: string;
  setEditValue: (v: string) => void;
  saveEdit: () => void;
  setEditing: (v: null) => void;
  startEdit: (id: string, field: EditField, value: string) => void;
  onVisChange: (id: string, field: string, value: boolean) => void;
  onDelete: (id: string, name: string) => void;
}> = ({ cls, groups, editing, editValue, setEditValue, saveEdit, setEditing, startEdit, onVisChange, onDelete }) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { saveEdit(); }
    else if (e.key === 'Escape') { setEditing(null); }
  };
  const groupName = groups.find(g => g._id === cls.groupId)?.name || '';
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 py-2 hover:bg-gray-50 gap-1">
      <div className="flex-1 pl-3 min-w-0">
        {editing?.id === cls._id && editing?.field === 'name' ? (
          <input autoFocus value={editValue} onChange={e => setEditValue(e.target.value)}
            onBlur={saveEdit} onKeyDown={handleKeyDown}
            className="border border-blue-300 rounded px-2 py-0.5 text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500" />
        ) : (
          <button type="button" className="text-sm text-gray-700 hover:text-blue-600 text-left w-full truncate" onDoubleClick={() => startEdit(cls._id, 'name', cls.name)}>
            {cls.name}
          </button>
        )}
        {editing?.id === cls._id && editing?.field === 'description' ? (
          <input autoFocus value={editValue} onChange={e => setEditValue(e.target.value)}
            onBlur={saveEdit} onKeyDown={handleKeyDown}
            className="border border-blue-300 rounded px-2 py-0.5 text-xs w-full mt-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        ) : (
          <button type="button" className="text-xs text-gray-400 hover:text-blue-400 text-left w-full truncate" onDoubleClick={() => startEdit(cls._id, 'description', cls.description || '')}>
            {cls.description || 'Clique duplo para adicionar descrição'}
          </button>
        )}
        {editing?.id === cls._id && editing?.field === 'groupId' ? (
          <div className="flex gap-1 mt-0.5">
            <select autoFocus value={editValue} onChange={e => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="border border-blue-300 rounded px-2 py-0.5 text-xs flex-1 focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="">— Sem agrupador —</option>
              {groups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
            </select>
            <button onClick={saveEdit} className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">✓</button>
            <button onClick={() => setEditing(null)} className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-100">✕</button>
          </div>
        ) : (
          <button type="button" className="text-xs text-purple-400 hover:text-purple-600 text-left w-full truncate" onDoubleClick={() => startEdit(cls._id, 'groupId', cls.groupId)}>
            📂 {groupName || 'Clique duplo para mover'}
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0 pl-3 sm:pl-0">
        <VisCheckbox label="Evo" checked={cls.showInEvolution !== false} onChange={v => onVisChange(cls._id, 'showInEvolution', v)} className="text-gray-500" />
        <VisCheckbox label="Orç" checked={cls.showInBudget !== false} onChange={v => onVisChange(cls._id, 'showInBudget', v)} className="text-gray-500" />
        <VisCheckbox label="Total" checked={cls.showInBudgetTotal !== false} onChange={v => onVisChange(cls._id, 'showInBudgetTotal', v)} className="text-gray-500" />
        <VisCheckbox label="Pag" checked={cls.showInPayment !== false} onChange={v => onVisChange(cls._id, 'showInPayment', v)} className="text-gray-500" />
        <VisCheckbox label="Prov" checked={cls.showInProvisioning !== false} onChange={v => onVisChange(cls._id, 'showInProvisioning', v)} className="text-gray-500" />
        <button onClick={() => onDelete(cls._id, cls.name)} className="text-red-400 hover:text-red-600 p-1 ml-auto sm:ml-0" title="Apagar">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

// === VisCheckbox ===
const VisCheckbox: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void; className?: string }> = ({ label, checked, onChange, className = '' }) => (
  <label className={`flex items-center gap-1 text-xs ${className}`}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="rounded w-3 h-3" />{' '}{label}
  </label>
);

// === Descriptions Tab ===

const DescriptionsTab: React.FC<{
  data: DescRow[];
  saving: boolean;
  setSaving: (v: boolean) => void;
  onReload: () => Promise<void>;
  groups: Group[];
  classifiers: Classifier[];
}> = ({ data, saving, setSaving, onReload, groups, classifiers }) => {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'description', dir: 'asc' });
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [batchModal, setBatchModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  const dragging = useRef(false);
  const dragStart = useRef(-1);
  const dragEnd = useRef(-1);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  const highlightRows = useCallback((start: number, end: number) => {
    const tbody = tbodyRef.current;
    if (!tbody) return;
    const min = Math.min(start, end);
    const max = Math.max(start, end);
    for (let i = 0; i < tbody.children.length; i++) {
      if (i >= min && i <= max) tbody.children[i].classList.add('bg-blue-50');
      else tbody.children[i].classList.remove('bg-blue-50');
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      if (!dragging.current) return;
      dragging.current = false;
      const range = getRange(dragStart.current, dragEnd.current);
      setSelectedRows(range);
      if (range.size >= 2) setBatchModal(true);
    };
    document.addEventListener('mouseup', handler);
    return () => document.removeEventListener('mouseup', handler);
  }, []);

  const filtered = useMemo(() => {
    let rows = data;
    for (const [key, vals] of Object.entries(filters)) {
      if (!vals.length) continue;
      const lowerVals = vals.map(v => v.toLowerCase());
      rows = rows.filter(r => lowerVals.includes(((r as any)[key] || '').toLowerCase()));
    }
    return [...rows].sort((a, b) => {
      const av = (a as any)[sort.key], bv = (b as any)[sort.key];
      const cmp = typeof av === 'number' ? av - bv : String(av || '').localeCompare(String(bv || ''));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [data, sort, filters]);

  const options = useMemo(() => ({
    description: distinct(data.map(r => r.description)),
    groupName: distinct(data.map(r => r.groupName)),
    classifierName: distinct(data.map(r => r.classifierName)),
  }), [data]);

  const toggleSort = (key: string) => setSort(p => p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const handleBatchApply = async (groupId: string, classifierId: string) => {
    const descriptions = Array.from(selectedRows).map(i => filtered[i]?.description).filter(Boolean);
    if (!descriptions.length) return;
    setSaving(true);
    setBatchModal(false);
    const fields: Record<string, string> = {};
    if (groupId) fields.agrupadorId = groupId;
    if (classifierId) fields.classificacaoId = classifierId;
    await categoryService.batchUpdateByDescription(descriptions, fields);
    await onReload();
    setSelectedRows(new Set());
    setSaving(false);
  };

  const cols = [
    { key: 'description', label: 'Descrição', filterable: true },
    { key: 'groupName', label: 'Agrupador', filterable: true },
    { key: 'classifierName', label: 'Classificação', filterable: true },
    { key: 'count', label: 'Qtd', filterable: false },
  ];

  return (
    <div className="px-4 pb-4">
      {/* Mobile Cards */}
      <div className="sm:hidden space-y-2">
        {filtered.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500">Nenhum resultado</div>
        ) : filtered.map((row, i) => (
          <button key={`${row.description}-${i}`} type="button"
            className={`border rounded-lg p-3 w-full text-left ${selectedRows.has(i) ? 'bg-blue-50 border-blue-300' : 'bg-white'}`}
            onClick={() => { setSelectedRows(new Set([i])); setBatchModal(true); }}>
            <p className="text-sm font-medium text-gray-800 truncate">{row.description}</p>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
              <span className="truncate">📂 {row.groupName || '—'}</span>
              <span className="truncate">🏷️ {row.classifierName || '—'}</span>
              <span className="shrink-0 ml-auto font-medium">{row.count}x</span>
            </div>
          </button>
        ))}
        <div className="text-xs text-gray-500 px-1">{filtered.length} de {data.length} registros</div>
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {cols.map(col => (
                <th key={col.key} className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap relative">
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggleSort(col.key)} className="hover:text-blue-600">
                      {col.label}{sort.key === col.key && (sort.dir === 'asc' ? ' ↑' : ' ↓')}
                    </button>
                    {col.filterable && (
                      <button onClick={() => setOpenFilter(openFilter === col.key ? null : col.key)}
                        className={`text-xs px-1 rounded ${filters[col.key]?.length ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:text-gray-600'}`}>▼</button>
                    )}
                  </div>
                  {openFilter === col.key && (
                    <FilterDropdown options={(options as any)[col.key] || []} selected={filters[col.key] || []}
                      onChange={v => { setFilters(p => ({ ...p, [col.key]: v })); setOpenFilter(null); }} onClose={() => setOpenFilter(null)} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody ref={tbodyRef} onMouseMove={e => {
            if (!dragging.current) return;
            const tr = (e.target as HTMLElement).closest('tr');
            if (!tr || !tbodyRef.current) return;
            const idx = Array.prototype.indexOf.call(tbodyRef.current.children, tr);
            if (idx < 0 || idx === dragEnd.current) return;
            dragEnd.current = idx;
            highlightRows(dragStart.current, idx);
          }}>
            {filtered.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">Nenhum resultado</td></tr>
            ) : filtered.map((row, i) => (
              <tr key={`${row.description}-${i}`}
                className={`border-b select-none cursor-pointer ${selectedRows.has(i) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                onMouseDown={e => { e.preventDefault(); dragging.current = true; dragStart.current = i; dragEnd.current = i; highlightRows(i, i); }}
                onDoubleClick={() => { setSelectedRows(new Set([i])); setBatchModal(true); }}>
                <td className="px-3 py-2">{row.description}</td>
                <td className="px-3 py-2">{row.groupName || <span className="text-gray-400">—</span>}</td>
                <td className="px-3 py-2">{row.classifierName || <span className="text-gray-400">—</span>}</td>
                <td className="px-3 py-2 text-right">{row.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 border-t">{filtered.length} de {data.length} registros</div>
      </div>

      {batchModal && (
        <BatchEditOverlay groups={groups} classifiers={classifiers}
          count={selectedRows.size} onApply={handleBatchApply} onCancel={() => { setBatchModal(false); setSelectedRows(new Set()); }} />
      )}
    </div>
  );
};

// --- BatchEditOverlay ---
const BatchEditOverlay: React.FC<{ count: number; groups: Group[]; classifiers: Classifier[]; onApply: (groupId: string, classifierId: string) => void; onCancel: () => void }> = ({ count, groups, classifiers, onApply, onCancel }) => {
  const [groupId, setGroupId] = useState('');
  const [classifierId, setClassifierId] = useState('');
  const filteredCls = groupId ? classifiers.filter(c => c.groupId === groupId) : classifiers;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 space-y-4">
        <h3 className="font-semibold text-gray-900">Edição em lote</h3>
        <p className="text-xs text-gray-500">{count} descrição(ões) selecionada(s)</p>
        <div className="space-y-3">
          <div>
            <label htmlFor="batch-agr" className="block text-xs font-medium text-gray-700 mb-1">Agrupador</label>
            <select id="batch-agr" value={groupId} onChange={e => { setGroupId(e.target.value); setClassifierId(''); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">— Selecione —</option>
              {groups.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="batch-cls" className="block text-xs font-medium text-gray-700 mb-1">Classificação</label>
            <select id="batch-cls" value={classifierId} onChange={e => setClassifierId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">— Selecione —</option>
              {filteredCls.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button onClick={() => onApply(groupId, classifierId)} disabled={!groupId && !classifierId} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Aplicar</button>
        </div>
      </div>
    </div>
  );
};

// --- FilterDropdown ---
const FilterDropdown: React.FC<{ options: string[]; selected: string[]; onChange: (v: string[]) => void; onClose: () => void }> = ({ options, selected, onChange, onClose }) => {
  const [local, setLocal] = useState<string[]>(selected);
  const [search, setSearch] = useState('');
  const toggle = (v: string) => setLocal(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]);
  const filteredOpts = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="absolute top-full left-0 z-50 mt-1 bg-white border rounded-lg shadow-lg p-2 min-w-[200px] max-h-[300px] flex flex-col">
      <div className="flex gap-1 mb-2">
        <button onClick={() => { onChange([]); }} className="text-xs text-blue-600 hover:underline">Limpar</button>
        <button onClick={() => { onChange(local); }} className="text-xs ml-auto bg-blue-600 text-white px-2 py-0.5 rounded">Aplicar</button>
        <button onClick={onClose} className="text-xs text-gray-500 hover:underline">✕</button>
      </div>
      <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..."
        className="w-full border border-gray-200 rounded px-2 py-1 text-xs mb-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
      <div className="overflow-y-auto flex-1">
        {filteredOpts.map(opt => (
          <label key={opt} className="flex items-center gap-2 py-0.5 text-xs cursor-pointer hover:bg-gray-50 rounded px-1">
            <input type="checkbox" checked={local.includes(opt)} onChange={() => toggle(opt)} className="rounded" />
            <span className="truncate">{opt || '(vazio)'}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default CategoriesPage;
